/* eslint-disable @typescript-eslint/no-explicit-any */

import { stringifyUnknown } from 'js/src/libs/custom-utils';

type AnyRecord = Record<string, any>;

const MISSING_LABEL_PLACEHOLDER = '<name>';

function firstOf<T>(value: T | T[] | undefined): T | undefined {
    return Array.isArray(value) ? value[0] : value;
}

function asArray<T>(value: T | T[] | undefined): T[] {
    if (value === undefined) return [];
    return Array.isArray(value) ? value : [value];
}

function stringField(obj: AnyRecord | undefined, key: string): string {
    const v = obj?.[key];
    return typeof v === 'string' ? v : '';
}

function seriesItemValue(item: unknown): number | string | null {
    if (typeof item === 'number' || typeof item === 'string') return item;
    if (item && typeof item === 'object' && 'value' in (item as AnyRecord)) {
        const v = (item as AnyRecord).value;
        if (typeof v === 'number' || typeof v === 'string') return v;
    }
    return null;
}

// `_skipDataMerge: true` marks a purely decorative series (e.g. BAR_CONFIG_3's
// background bar). Don't filter on `silent` — that just disables mouse events
// and is set on real data series too (e.g. PIE_CONFIG_2's pies).
function isDecorativeSeries(series: AnyRecord): boolean {
    return series._skipDataMerge === true;
}

// `: ` and `: , ,` carry no info — drop those rows. Anything outside of
// colons, commas, and whitespace counts as content.
function isStructurallyEmpty(line: string): boolean {
    return line.replace(/[:,\s]/g, '') === '';
}

// `<name>` only appears where there are values to pair with — otherwise the
// row is bare and will be dropped by isStructurallyEmpty.
function labelOrPlaceholder(name: string, hasValues: boolean): string {
    if (name.length > 0) return name;
    return hasValues ? MISSING_LABEL_PLACEHOLDER : '';
}

function extractTitleLine(echarts: AnyRecord): string | null {
    const text = stringField(firstOf<AnyRecord>(echarts.title), 'text');
    return text ? text.replace(/\s*\n\s*/g, ' ').trim() : null;
}

function extractPieLines(pieSeries: AnyRecord): string[] {
    const data = Array.isArray(pieSeries.data) ? pieSeries.data : [];
    return data.map((item: any) => {
        const value = seriesItemValue(item);
        return `${labelOrPlaceholder(stringField(item, 'name'), value !== null)}: ${value ?? ''}`;
    });
}

function extractCartesianLines(echarts: AnyRecord, series: AnyRecord[]): string[] {
    const xAxis = firstOf<AnyRecord>(echarts.xAxis);
    const yAxis = firstOf<AnyRecord>(echarts.yAxis);
    const categoryAxis =
        xAxis?.type === 'category' ? xAxis : yAxis?.type === 'category' ? yAxis : null;

    // Pivot single-series to `<category>: <value>` rows; the multi-series
    // layout below reads awkwardly with one row of values.
    if (
        series.length === 1 &&
        categoryAxis &&
        Array.isArray(categoryAxis.data) &&
        categoryAxis.data.length > 0
    ) {
        const [s] = series;
        const seriesName = stringField(s, 'name');
        const data = Array.isArray(s.data) ? s.data : [];
        const lines: string[] = seriesName ? [seriesName] : [];
        categoryAxis.data.forEach((cat: unknown, i: number) => {
            const catLabel = stringifyUnknown(cat);
            const value = seriesItemValue(data[i]);
            lines.push(`${labelOrPlaceholder(catLabel, value !== null)}: ${value ?? ''}`);
        });
        return lines;
    }

    const lines: string[] = [];

    if (categoryAxis) {
        const data = Array.isArray(categoryAxis.data) ? categoryAxis.data : [];
        const hasValues = data.some((v: unknown) => v != null && v !== '');
        const label = labelOrPlaceholder(stringField(categoryAxis, 'name'), hasValues);
        lines.push(`${label}: ${data.join(', ')}`);
    }

    for (const s of series) {
        const rendered = (Array.isArray(s.data) ? s.data : []).map(seriesItemValue);
        const hasValues = rendered.some((v) => v !== null);
        const values = rendered.map((v) => v ?? '').join(', ');
        lines.push(`${labelOrPlaceholder(stringField(s, 'name'), hasValues)}: ${values}`);
    }

    return lines;
}

/**
 * Render an ECharts config as the structured text shown in the Chart Settings
 * "Data" textarea: a `<Title>` line (if any), then one `<label>: <value(s)>`
 * row per slice or series. See chart-data-extract.spec.ts for the exact format
 * per chart shape.
 */
export function extractChartConfigToText(echarts: unknown): string {
    if (!echarts || typeof echarts !== 'object') return '';
    const config = echarts as AnyRecord;

    const allSeries = asArray<AnyRecord>(config.series).filter(
        (s) => s && typeof s === 'object' && !isDecorativeSeries(s),
    );

    const titleLine = extractTitleLine(config);
    const pieSeries = allSeries.find((s) => s.type === 'pie');
    const dataLines = (
        pieSeries ? extractPieLines(pieSeries) : extractCartesianLines(config, allSeries)
    ).filter((line) => !isStructurallyEmpty(line));

    return (titleLine ? [titleLine, ...dataLines] : dataLines).join('\n');
}
